import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { getEvent, createInvitation, listInvitations, rotateEventId, fetchEventQrDataUrl } from '@/lib/events-api';
import { ArrowLeft, CheckCircle, Copy, Key, RotateCw } from 'lucide-react';

export default function EventCreatedPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const eventId = sp.get('event_id') || '';
  const { ready } = useRequireAuth();

  const [event, setEvent] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedLabel, setCopiedLabel] = useState(null);
  const [rotating, setRotating] = useState(false);

  const loadInvite = useCallback(async (evtId) => {
    const list = await listInvitations(evtId);
    const active = (list.invitations || []).find(i => !i.revoked_at);
    if (active) return active;
    const created = await createInvitation(evtId, { channel: 'link', uses_remaining: null });
    return created;
  }, []);

  useEffect(() => {
    if (!eventId || !ready) return;
    (async () => {
      setLoading(true); setError(null);
      try {
        const eb = await getEvent(eventId);
        setEvent(eb.event);
        const inv = await loadInvite(eventId);
        setInvite(inv);
        const qr = await fetchEventQrDataUrl(eventId, 'event_id');
        setQrDataUrl(qr);
      } catch (e) { setError(e.message || 'Could not load event.'); }
      finally { setLoading(false); }
    })();
  }, [eventId, ready, loadInvite]);

  async function doCopy(text, label) {
    try { await navigator.clipboard.writeText(text); setCopiedLabel(label); setTimeout(() => setCopiedLabel(null), 1800); } catch {}
  }

  async function onRotate() {
    if (!event) return;
    setRotating(true);
    try {
      const r = await rotateEventId(event.id);
      const eb = await getEvent(event.id);
      setEvent(eb.event);
      const qr = await fetchEventQrDataUrl(event.id, 'event_id');
      setQrDataUrl(qr);
      setCopiedLabel(`Rotated → ${r.current_event_id}`);
      setTimeout(() => setCopiedLabel(null), 2500);
    } catch (e) { setError(e.message || 'Could not rotate Event ID.'); }
    finally { setRotating(false); }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="event-created-screen">
      <button onClick={() => navigate('/host-dashboard', { replace: true })} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="created-back-btn">
        <ArrowLeft className="w-4 h-4" /> back to dashboard
      </button>

      {loading ? (
        <div className="flex justify-center py-8" data-testid="created-loading"><div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" /></div>
      ) : error ? (
        <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-xl p-4" data-testid="created-error"><p className="text-[#C93434]">{error}</p></div>
      ) : event ? (
        <div className="flex flex-col gap-4">
          <div className="bg-[#E4F4EC] border border-[#1F8A55]/20 rounded-xl p-4 flex items-start gap-3" data-testid="created-success-banner">
            <CheckCircle className="w-6 h-6 text-[#1F8A55] flex-shrink-0" />
            <div>
              <h1 className="text-xl font-extrabold text-[#0B1B46]">Event ready to share</h1>
              <p className="text-sm text-[#5B6472]">Invite guests using the Event ID, QR code, or invite link below.</p>
            </div>
          </div>

          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="created-event-card">
            <h2 className="text-xl font-extrabold text-[#0B1B46]" data-testid="created-event-name">{event.name}</h2>
            <div className="flex gap-2 mt-2">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#E4F4EC] text-[#1F8A55]" data-testid="created-status">{event.status}</span>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#EFF5FF] text-[#146BFF]">{event.category}</span>
            </div>
          </div>

          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="created-event-id-card">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-3">Event ID</p>
            <div className="flex items-center gap-3">
              <span className="flex-1 text-center text-2xl font-extrabold font-mono tracking-[3px] bg-[#F3F5F8] py-3 px-4 rounded-lg text-[#0B1B46]" data-testid="created-event-code">{event.current_event_id}</span>
              <button onClick={() => doCopy(event.current_event_id || '', 'Event ID')} className="p-2.5 border border-[#146BFF] rounded-lg hover:bg-[#EFF5FF] transition-colors" data-testid="created-copy-code-btn">
                <Copy className="w-4 h-4 text-[#146BFF]" />
              </button>
            </div>
            <p className="text-xs text-[#5B6472] mt-2">Contributors can enter this on the Join Event screen.</p>
            <button onClick={onRotate} disabled={rotating}
              className="w-full h-10 mt-3 flex items-center justify-center gap-2 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] disabled:opacity-50 transition-colors"
              data-testid="created-rotate-btn">
              <RotateCw className="w-4 h-4" /> {rotating ? 'Rotating…' : 'Rotate Event ID'}
            </button>
          </div>

          {qrDataUrl && (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="created-qr-card">
              <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-3">QR Code</p>
              <div className="flex justify-center py-4 bg-white rounded-lg">
                <img src={qrDataUrl} alt="Event QR Code" className="w-[220px] h-[220px] object-contain" data-testid="created-qr-image" />
              </div>
              <p className="text-xs text-[#5B6472] mt-2">Print or display for in-person guests to scan.</p>
            </div>
          )}

          {invite && (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="created-invite-card">
              <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-3">Invite Link</p>
              <p className="text-xs font-mono text-[#146BFF] bg-[#F3F5F8] p-3 rounded-lg break-all" data-testid="created-invite-link">{invite.link || ''}</p>
              <div className="flex gap-3 mt-3">
                <button onClick={() => doCopy(invite.link || '', 'Invite link')} className="flex-1 flex items-center justify-center gap-2 h-10 border border-[#146BFF] rounded-lg text-[#146BFF] font-bold text-sm hover:bg-[#EFF5FF] transition-colors" data-testid="created-copy-link-btn">
                  <Copy className="w-4 h-4" /> Copy
                </button>
                <button onClick={() => doCopy(invite.token || '', 'Token')} className="flex-1 flex items-center justify-center gap-2 h-10 border border-[#146BFF] rounded-lg text-[#146BFF] font-bold text-sm hover:bg-[#EFF5FF] transition-colors" data-testid="created-copy-token-btn">
                  <Key className="w-4 h-4" /> Copy token
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#EFF5FF] text-[#146BFF]">{invite.uses_remaining == null ? 'Multi-use' : `${invite.uses_remaining} uses left`}</span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${invite.expires_at ? 'bg-[#FBEEDA] text-[#B86500]' : 'bg-[#E4F4EC] text-[#1F8A55]'}`}>{invite.expires_at ? `Expires ${new Date(invite.expires_at).toLocaleDateString()}` : 'No expiry'}</span>
              </div>
            </div>
          )}

          {copiedLabel && (
            <div className="bg-[#E4F4EC] border border-[#1F8A55]/20 rounded-lg p-3 text-[#1F8A55] font-bold text-sm" data-testid="created-copied-toast">✓ {copiedLabel} copied</div>
          )}

          <button onClick={() => navigate('/host-dashboard', { replace: true })}
            className="h-11 bg-[#146BFF] hover:bg-[#0F55CC] text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
            data-testid="created-done-btn">Back to Dashboard</button>
        </div>
      ) : null}
    </div>
  );
}
